/*
 * Product comparison — <compare-drawer>
 * A single global custom element that keeps the selected products in localStorage
 * and drives a left-edge compare tray: a slim tab pinned to the left-middle edge
 * (icon + count) that slides out a panel with each product's image + title, a
 * clear-all and close control, and a "Compare now" CTA. On /pages/compare it also
 * renders the side-by-side grid. Fully client-side: each PDP prints the product's
 * specs as a JSON blob (snippet 'compare-toggle'), so no network requests.
 */
if (!customElements.get('compare-drawer')) {
  class CompareDrawer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.storageKey = this.dataset.storageKey || 'theinspirehome:compare';
      this.compareUrl = this.dataset.compareUrl || '/pages/compare';
      this.maxDesktop = parseInt(this.dataset.maxDesktop, 10) || 4;
      this.maxMobile = parseInt(this.dataset.maxMobile, 10) || 3;
      this.removeLabel = this.dataset.removeLabel || 'Remove';
      this.slotEmpty = this.dataset.slotEmpty || '';
      this.isOpen = false;

      this.tab = this.querySelector('[data-compare-open]');
      this.listEl = this.querySelector('[data-compare-list]');
      this.emptyEl = this.querySelector('[data-compare-empty]');
      this.goBtn = this.querySelector('[data-compare-go]');
      this.goCount = this.querySelector('[data-go-count]');
      this.needEl = this.querySelector('[data-compare-need]');
      this.countEls = this.querySelectorAll('[data-tab-count]');

      const closeTpl = this.querySelector('[data-compare-close-icon]');
      this.closeIcon = closeTpl ? closeTpl.innerHTML : '&times;';

      try {
        this.rows = JSON.parse(this.querySelector('[data-compare-rows]').textContent) || [];
      } catch (e) {
        this.rows = [];
      }

      this.onComparePage = !!document.querySelector('[data-compare-view]');
      // Initialise the media query BEFORE the first enforceMax()/render() so the
      // mobile column cap (maxMobile) applies on load — otherwise currentMax()
      // sees this.mq === undefined and falls back to maxDesktop, and no `change`
      // event fires when the page loads already at a mobile width.
      this.mq = window.matchMedia('(max-width: 749px)');
      this.selected = this.readStore();
      this.enforceMax();

      this.bind();
      this.syncToggles();
      this.render();

      const onMq = () => { this.enforceMax(); this.syncToggles(); this.render(); };
      if (this.mq.addEventListener) this.mq.addEventListener('change', onMq);
      else if (this.mq.addListener) this.mq.addListener(onMq);

      window.addEventListener('storage', (e) => {
        if (e.key !== this.storageKey) return;
        this.selected = this.readStore();
        this.enforceMax();
        this.syncToggles();
        this.render();
      });

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => { this.syncToggles(); this.render(); });
      }
    }

    /* ---- storage ---- */
    readStore() {
      let list = [];
      try { list = JSON.parse(localStorage.getItem(this.storageKey)) || []; } catch (e) {}
      if (!Array.isArray(list)) list = [];
      return list.filter((x) => x && x.id != null && x.attrs);
    }

    writeStore() {
      try { localStorage.setItem(this.storageKey, JSON.stringify(this.selected)); } catch (e) {}
    }

    currentMax() { return this.mq && this.mq.matches ? this.maxMobile : this.maxDesktop; }
    atMax() { return this.selected.length >= this.currentMax(); }

    enforceMax() {
      const max = this.currentMax();
      if (this.selected.length > max) {
        this.selected = this.selected.slice(0, max);
        this.writeStore();
      }
    }

    /* ---- open / close (left-edge panel) ---- */
    open() {
      if (this.onComparePage || !this.selected.length) return;
      this.isOpen = true;
      this.classList.add('is-open');
      if (this.tab) this.tab.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.isOpen = false;
      this.classList.remove('is-open');
      if (this.tab) this.tab.setAttribute('aria-expanded', 'false');
    }

    /* ---- events (delegated) ---- */
    bind() {
      document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-compare-add]');
        if (addBtn) {
          const item = addBtn.closest('[data-compare-item]');
          if (item) this.onToggle(item);
          return;
        }
        if (e.target.closest('[data-compare-open]')) { this.open(); return; }
        if (e.target.closest('[data-compare-close]')) { this.close(); return; }
        const removeBtn = e.target.closest('[data-compare-remove]');
        if (removeBtn) { this.removeById(removeBtn.dataset.id); return; }
        if (e.target.closest('[data-compare-clear]')) { this.clearAll(); return; }
        if (e.target.closest('[data-compare-go]')) { this.goToCompare(); return; }
        // click outside the tray closes it
        if (this.isOpen && !e.target.closest('.compare-edge')) this.close();
      });

      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });
    }

    onToggle(item) {
      const id = item.dataset.id;
      if (this.selected.some((x) => String(x.id) === String(id))) { this.removeById(id); return; }
      if (this.atMax()) return;
      const data = this.readItemData(item);
      if (!data) return;
      this.selected.push(data);
      this.writeStore();
      this.syncToggles();
      this.render();
      requestAnimationFrame(() => requestAnimationFrame(() => this.open()));
    }

    readItemData(item) {
      const node = item.querySelector('[data-compare-data]');
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (e) { return null; }
    }

    removeById(id) {
      this.selected = this.selected.filter((x) => String(x.id) !== String(id));
      this.writeStore();
      this.syncToggles();
      this.render();
    }

    clearAll() {
      this.selected = [];
      this.writeStore();
      this.close();
      this.syncToggles();
      this.render();
    }

    goToCompare() {
      if (this.selected.length < 2) return;
      window.location.href = this.compareUrl;
    }

    /* ---- reflect selection onto every PDP toggle on the page ---- */
    syncToggles() {
      const full = this.atMax();
      document.querySelectorAll('[data-compare-item]').forEach((item) => {
        const id = item.dataset.id;
        const isSel = this.selected.some((x) => String(x.id) === String(id));
        item.classList.toggle('is-added', isSel);
        const btn = item.querySelector('[data-compare-add]');
        if (btn) {
          btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
          btn.disabled = !isSel && full;
        }
        const addLabel = item.querySelector('[data-add-label]');
        const addedLabel = item.querySelector('[data-added-label]');
        if (addLabel) addLabel.hidden = isSel;
        if (addedLabel) addedLabel.hidden = !isSel;
      });
    }

    /* ---- render tray + counts + compare page ---- */
    render() {
      const n = this.selected.length;

      if (this.listEl) {
        this.listEl.innerHTML = '';
        const max = this.currentMax();
        for (let i = 0; i < max; i++) {
          const it = this.selected[i];
          this.listEl.appendChild(it ? this.buildSlot(it) : this.buildEmptySlot());
        }
      }
      if (this.emptyEl) this.emptyEl.hidden = n !== 0;
      if (this.countEls) this.countEls.forEach((el) => { el.textContent = n ? String(n) : ''; });
      if (this.goCount) this.goCount.textContent = n >= 2 ? String(n) : '';
      if (this.goBtn) this.goBtn.hidden = n < 2;
      if (this.needEl) this.needEl.hidden = n !== 1;

      // The whole tray is hidden with no selection, or on the compare page itself.
      if (n === 0 || this.onComparePage) {
        if (this.isOpen) this.close();
        this.hidden = true;
      } else {
        this.hidden = false;
      }

      const view = document.querySelector('[data-compare-view]');
      if (view) this.buildComparison(view.querySelector('[data-compare-grid]'));
    }

    buildSlot(it) {
      const slot = document.createElement('div');
      slot.className = 'compare-edge__slot';

      const link = document.createElement('a');
      link.className = 'compare-edge__slot-link';
      link.href = it.url || '#';

      if (it.image) {
        const img = new Image();
        img.className = 'compare-edge__slot-img';
        img.src = it.image;
        img.alt = it.title || '';
        img.loading = 'lazy';
        link.appendChild(img);
      }

      const title = document.createElement('span');
      title.className = 'compare-edge__slot-title';
      title.textContent = it.title || '';
      link.appendChild(title);
      slot.appendChild(link);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'compare-edge__slot-remove';
      remove.setAttribute('data-compare-remove', '');
      remove.setAttribute('aria-label', this.removeLabel);
      remove.dataset.id = it.id;
      remove.innerHTML = this.closeIcon;
      slot.appendChild(remove);

      return slot;
    }

    buildEmptySlot() {
      const slot = document.createElement('div');
      slot.className = 'compare-edge__slot compare-edge__slot--empty';
      const box = document.createElement('span');
      box.className = 'compare-edge__slot-box';
      box.setAttribute('aria-hidden', 'true');
      box.textContent = '+';
      slot.appendChild(box);
      const label = document.createElement('span');
      label.className = 'compare-edge__slot-title';
      label.textContent = this.slotEmpty || 'Add a product';
      slot.appendChild(label);
      return slot;
    }

    /* ---- /pages/compare grid ---- */
    buildComparison(grid) {
      if (!grid) return;
      const dash = '—';
      const cols = this.currentMax();
      const activeCount = this.selected.length;
      const slots = [];
      for (let s = 0; s < cols; s++) slots.push(this.selected[s] || null);

      grid.style.setProperty('--compare-cols', String(cols));
      grid.innerHTML = '';

      // Header row: corner + one product/empty head per slot. The corner carries a
      // child span so it is never :empty — the theme's CDN CSS hides truly-empty
      // cells (display:none), which would shift the grid.
      const corner = document.createElement('div');
      corner.className = 'compare-table__corner';
      corner.appendChild(document.createElement('span'));
      grid.appendChild(corner);
      slots.forEach((slot) => grid.appendChild(slot ? this.buildHead(slot) : this.buildEmptyHead(activeCount)));

      // Spec rows.
      this.rows.forEach((row) => {
        const label = document.createElement('div');
        label.className = 'compare-table__rowlabel';
        label.textContent = row.label;
        grid.appendChild(label);

        slots.forEach((slot) => {
          const cell = document.createElement('div');
          cell.className = 'compare-table__val';

          // Per-cell label — hidden on desktop (the sticky label column shows it),
          // revealed on mobile where the label column is dropped so products sit
          // side by side with each spec labelled above its value.
          const lbl = document.createElement('span');
          lbl.className = 'compare-table__val-label';
          lbl.textContent = row.label;
          cell.appendChild(lbl);

          const content = document.createElement('div');
          content.className = 'compare-table__val-content';
          if (!slot) {
            cell.classList.add('compare-table__val--empty');
            content.textContent = dash;
          } else if (row.key === 'sizes') {
            cell.classList.add('compare-table__val--sizes');
            content.appendChild(this.buildSizeList(slot, dash));
          } else {
            const real = slot.attrs && slot.attrs[row.key];
            content.textContent = real != null && String(real).trim() !== '' ? real : dash;
          }
          cell.appendChild(content);
          grid.appendChild(cell);
        });
      });
    }

    buildHead(it) {
      const head = document.createElement('div');
      head.className = 'compare-table__head';

      // Image carousel — all product images, contained (fully visible). Arrows
      // are shown on desktop only (CSS); mobile swipes via scroll-snap.
      const imgs = Array.isArray(it.images) && it.images.length ? it.images : it.image ? [it.image] : [];
      const carousel = document.createElement('div');
      carousel.className = 'compare-carousel';

      const track = document.createElement('div');
      track.className = 'compare-carousel__track';
      imgs.forEach((src) => {
        const slide = document.createElement('a');
        slide.className = 'compare-carousel__slide';
        slide.href = it.url || '#';
        const img = new Image();
        img.src = src;
        img.alt = it.title || '';
        img.loading = 'lazy';
        slide.appendChild(img);
        track.appendChild(slide);
      });
      carousel.appendChild(track);

      if (imgs.length > 1) {
        ['prev', 'next'].forEach((dir) => {
          const arrow = document.createElement('button');
          arrow.type = 'button';
          arrow.className = 'compare-carousel__arrow compare-carousel__arrow--' + dir;
          arrow.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
          arrow.innerHTML = dir === 'prev' ? '&#8249;' : '&#8250;';
          arrow.addEventListener('click', (e) => {
            e.preventDefault();
            track.scrollBy({ left: (dir === 'prev' ? -1 : 1) * track.clientWidth, behavior: 'smooth' });
          });
          carousel.appendChild(arrow);
        });
      }
      head.appendChild(carousel);

      const name = document.createElement('a');
      name.className = 'compare-table__name';
      name.href = it.url || '#';
      name.textContent = it.title || '';
      head.appendChild(name);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'compare-table__remove';
      remove.setAttribute('data-compare-remove', '');
      remove.setAttribute('aria-label', this.removeLabel);
      remove.dataset.id = it.id;
      remove.innerHTML = this.closeIcon;
      head.appendChild(remove);

      return head;
    }

    buildEmptyHead(activeCount) {
      const cell = document.createElement('div');
      cell.className = 'compare-table__head compare-table__head--empty';
      const msg = document.createElement('span');
      msg.className = 'compare-table__empty-msg';
      msg.textContent = this.slotEmpty;
      cell.appendChild(msg);
      return cell;
    }

    buildSizeList(slot, dash) {
      const wrap = document.createElement('div');
      wrap.className = 'compare-sizes';
      const list = slot.variants || [];
      if (!list.length) { wrap.textContent = dash; return wrap; }
      list.forEach((v) => {
        const r = document.createElement('div');
        r.className = 'compare-sizes__row';
        const s = document.createElement('span');
        s.className = 'compare-sizes__size';
        s.textContent = v.size || '';
        const p = document.createElement('span');
        p.className = 'compare-sizes__price';
        p.textContent = v.price || '';
        r.appendChild(s);
        r.appendChild(p);
        wrap.appendChild(r);
      });
      return wrap;
    }
  }

  customElements.define('compare-drawer', CompareDrawer);
}
